/**
 * PendientesPage — Mis seguimientos pendientes agrupados por urgencia.
 *
 * Cada tarjeta muestra: agencia/prospecto, acción pendiente, fecha esperada,
 * notas de la visita previa. Botón "Registrar visita" abre VisitaForm pre-cargado.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Paper, Stack, Tabs, Tab,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BusinessIcon from '@mui/icons-material/Business';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AddTaskIcon from '@mui/icons-material/AddTask';
import VisitaForm from './VisitaForm';
import {
  API_BASE, getEmpresaId, getAuthHeaders, fechaRelativa, formatFechaCorta,
} from './format';

const BUCKETS = [
  { key: 'atrasados', label: '🔴 Atrasados',  color: '#dc2626', emptyMsg: '¡Sin atrasos!' },
  { key: 'hoy',       label: '⚡ Hoy',         color: '#f59e0b', emptyMsg: 'Nada para hoy' },
  { key: 'manana',    label: '📅 Mañana',     color: '#0ea5e9', emptyMsg: 'Sin tareas para mañana' },
  { key: 'semana',    label: '🗓️ Esta semana', color: '#8b5cf6', emptyMsg: 'Sin tareas en la semana' },
];

export default function PendientesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [visitaPrefill, setVisitaPrefill] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/comercial/pendientes?empresa_id=${getEmpresaId()}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setData(d);
    } catch { setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  // Auto-seleccionar bucket con tareas
  useEffect(() => {
    if (!data) return;
    for (let i = 0; i < BUCKETS.length; i++) {
      if ((data[BUCKETS[i].key] || []).length > 0) { setTab(i); return; }
    }
  }, [data?.totales]); // eslint-disable-line

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Mis pendientes</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Seguimientos que tienes que ejecutar.
          {data && data.total_general > 0 && (
            <> {' · '}<b style={{ color: '#dc2626' }}>{data.total_general} en total</b></>
          )}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
      ) : !data || data.total_general === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <AddTaskIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Nada pendiente 🎉</Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
            Cuando registres una visita con "siguiente acción", aparecerá aquí.
          </Typography>
        </Paper>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ mb: 2, borderBottom: '1px solid #e2e8f0',
                  '& .MuiTabs-indicator': { backgroundColor: BUCKETS[tab].color } }}>
            {BUCKETS.map(b => (
              <Tab key={b.key}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{b.label}</span>
                    <Chip size="small" label={data.totales[b.key] || 0}
                      sx={{ height: 18, fontSize: 10, fontWeight: 700,
                             bgcolor: data.totales[b.key] > 0 ? b.color : '#e2e8f0',
                             color: data.totales[b.key] > 0 ? '#fff' : '#64748b' }} />
                  </Stack>
                }
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, minHeight: 44 }} />
            ))}
          </Tabs>

          <Stack spacing={1}>
            {(data[BUCKETS[tab].key] || []).map(v => (
              <Paper key={v.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                      {v.agencia_id
                        ? <BusinessIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
                        : <PersonSearchIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />}
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {v.agencia_nombre || v.prospecto_nombre}
                      </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <WhatshotIcon sx={{ fontSize: 14, color: BUCKETS[tab].color }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {v.siguiente_accion}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                      Para: <b>{formatFechaCorta(v.siguiente_accion_fecha)}</b>
                      {' · '}Última visita: {fechaRelativa(v.fecha_visita)}
                    </Typography>
                    {v.notas && (
                      <Typography sx={{ fontSize: 11.5, color: '#475569', mt: 0.5,
                                         display: '-webkit-box', WebkitLineClamp: 2,
                                         WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        💬 {v.notas}
                      </Typography>
                    )}
                  </Box>
                  <Button size="small" variant="contained" disableElevation
                    startIcon={<EventIcon />}
                    onClick={() => setVisitaPrefill({
                      agencia_id: v.agencia_id, agencia_nombre: v.agencia_nombre,
                      prospecto_id: v.prospecto_id, prospecto_nombre: v.prospecto_nombre,
                    })}
                    sx={{ textTransform: 'none',
                          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                    Ejecutar
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}

      {visitaPrefill && (
        <VisitaForm open onClose={() => setVisitaPrefill(null)}
          prefill={visitaPrefill}
          onSaved={() => { setVisitaPrefill(null); cargar(); }} />
      )}
    </Box>
  );
}
